import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaCar, FaEdit, FaCheckCircle, FaTimesCircle, FaEye, FaClipboardList, FaGolfBall, FaTruck, FaPlus, FaSearch, FaFilter, FaDownload, FaUpload } from 'react-icons/fa';
import { supabase } from '../../lib/supabase';
import { vehicleService } from '../../services/vehicleService';
// import AddVehicleForm from './AddVehicleForm'; // Removed - using internal form
import { formatVehicleTitle } from '../../utils/textFormatting';
// import '../../styles/admin.css'; // Removed - file doesn't exist
import vinDecoderService from '../../services/vinDecoderService';
import VideoUpload from './VideoUpload';
import ImageUpload from './ImageUpload';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiEdit, FiTrash2, FiSearch, FiFilter, FiEye, FiRefreshCw, FiX, FiCheck } = FiIcons;
const VehicleManager = ({ unitType = 'vehicle' }) => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [decodingVIN, setDecodingVIN] = useState(false);
  const [vinError, setVinError] = useState(null);
  
  // Batch image optimization state
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState({ current: 0, total: 0, currentVehicle: '', currentImage: 0, totalImages: 0, skipped: 0, converted: 0 });
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);

  const getUnitTitle = () => {
    switch(unitType) {
      case 'golf-cart': return 'Golf Carts';
      case 'trailer': return 'Trailers';
      case 'boat': return 'Boats';
      default: return 'Vehicles';
    }
  };

  const [vehicleForm, setVehicleForm] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    condition: 'new',
    status: 'on-lot',
    vehicleType: unitType,
    vin: '',
    carfaxUrl: '',
    stockNumber: '',
    fuelType: 'gasoline',
    transmission: 'automatic',
    exteriorColor: '',
    interiorColor: '',
    engine: '',
    drivetrain: 'fwd',
    description: '',
    features: [],
    images: [],
    videos: []
  });

  // Load vehicles from Supabase on component mount
  useEffect(() => {
    loadVehicles();
  }, [unitType]);

  const loadVehicles = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await vehicleService.getAllVehicles();
      console.log('Raw vehicles from DB:', data);
      console.log('Current unitType:', unitType);
      
      // Filter by unit type with safe fallback rules
      const filtered = (data || []).filter(v => {
        const rawType = v.vehicle_type || v.vehicleType || '';
        const vType = typeof rawType === 'string' ? rawType.toLowerCase() : '';

        if (unitType === 'vehicle') {
          // In the Vehicles tab, show anything that is not explicitly a non-vehicle type
          // This ensures older rows with null/unknown types still appear
          return !['golf-cart', 'trailer', 'boat'].includes(vType) || vType === '';
        }

        // For specific tabs, require exact match
        return vType === unitType;
      });
      
      setVehicles(filtered);
      setFilteredVehicles(filtered);
      
      console.log('Filtered vehicles for', unitType, ':', filtered.length);
    } catch (err) {
      setError('Failed to load vehicles: ' + err.message);
      console.error('Error loading vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  // Note: Sample vehicle data structure for reference only - actual data loaded from Supabase
  // Sample structure: { id, make, model, year, price, mileage, condition, status, vin, stockNumber, fuelType, transmission, exteriorColor, interiorColor, engine, drivetrain, description, features, images, createdAt }

  useEffect(() => {
    let filtered = vehicles;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(vehicle =>
        vehicle.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.stock_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.stockNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.status === statusFilter);
    }

    setFilteredVehicles(filtered);
    console.log('Filtered vehicles:', filtered.length);
  }, [vehicles, searchTerm, statusFilter]);

  const handleAddVehicle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clean up form data - convert empty strings to null for integer fields
      const cleanedForm = {
        ...vehicleForm,
        year: vehicleForm.year ? parseInt(vehicleForm.year) : new Date().getFullYear(),
        price: vehicleForm.price ? parseFloat(vehicleForm.price) : 0,
        mileage: vehicleForm.mileage ? parseInt(vehicleForm.mileage) : 0,
        doors: vehicleForm.doors ? parseInt(vehicleForm.doors) : null,
        engineCylinders: vehicleForm.engineCylinders ? parseInt(vehicleForm.engineCylinders) : null,
        engineHP: vehicleForm.engineHP ? parseInt(vehicleForm.engineHP) : null,
        cityMPG: vehicleForm.cityMPG ? parseInt(vehicleForm.cityMPG) : null,
        highwayMPG: vehicleForm.highwayMPG ? parseInt(vehicleForm.highwayMPG) : null,
        combinedMPG: vehicleForm.combinedMPG ? parseInt(vehicleForm.combinedMPG) : null,
        capacity: vehicleForm.capacity ? parseInt(vehicleForm.capacity) : null,
        length: vehicleForm.length ? parseFloat(vehicleForm.length) : null,
        width: vehicleForm.width ? parseFloat(vehicleForm.width) : null,
        images: vehicleForm.images || [],
        videos: vehicleForm.videos || [],
        features: vehicleForm.features || [],
      };
      
      const { data: newVehicle, error: createError } = await vehicleService.createVehicle(cleanedForm);
      if (createError || !newVehicle) {
        const message = createError?.message || 'Unknown error';
        setError('Failed to add vehicle: ' + message);
        alert('Failed to add vehicle: ' + message);
        return;
      }
      setVehicles([...vehicles, newVehicle]);
      setShowAddModal(false);
      resetForm();
      alert('Vehicle added successfully!');
    } catch (err) {
      setError('Failed to add vehicle: ' + err.message);
      console.error('Error adding vehicle:', err);
      alert('Failed to add vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditVehicle = (vehicle) => {
    setEditingVehicle(vehicle);
    // Map DB snake_case to form-friendly fields where needed
    setVehicleForm({
      ...vehicle,
      vehicleType: vehicle.vehicleType || vehicle.vehicle_type || 'vehicle',
      images: vehicle.images || [],
      videos: vehicle.videos || [],
      // Ensure trailer fields are populated
      length: vehicle.length || '',
      width: vehicle.width || '',
      capacity: vehicle.capacity || '',
      axles: vehicle.axles || '1',
      trailerType: vehicle.trailerType || vehicle.trailer_type || 'utility'
    });
    // Don't set showAddModal for edits - use editingVehicle state instead
  };

  const handleUpdateVehicle = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Clean up form data - convert empty strings to null for integer fields
      const cleanedForm = {
        ...vehicleForm,
        year: vehicleForm.year ? parseInt(vehicleForm.year) : new Date().getFullYear(),
        price: vehicleForm.price ? parseFloat(vehicleForm.price) : 0,
        mileage: vehicleForm.mileage ? parseInt(vehicleForm.mileage) : 0,
        doors: vehicleForm.doors ? parseInt(vehicleForm.doors) : null,
        engineCylinders: vehicleForm.engineCylinders ? parseInt(vehicleForm.engineCylinders) : null,
        engineHP: vehicleForm.engineHP ? parseInt(vehicleForm.engineHP) : null,
        cityMPG: vehicleForm.cityMPG ? parseInt(vehicleForm.cityMPG) : null,
        highwayMPG: vehicleForm.highwayMPG ? parseInt(vehicleForm.highwayMPG) : null,
        combinedMPG: vehicleForm.combinedMPG ? parseInt(vehicleForm.combinedMPG) : null,
        capacity: vehicleForm.capacity ? parseInt(vehicleForm.capacity) : null,
        length: vehicleForm.length ? parseFloat(vehicleForm.length) : null,
        width: vehicleForm.width ? parseFloat(vehicleForm.width) : null,
        images: vehicleForm.images || [],
        videos: vehicleForm.videos || [],
        features: vehicleForm.features || [],
      };
      
      const { data: updatedVehicle, error: updateError } = await vehicleService.updateVehicle(editingVehicle.id, cleanedForm);
      if (updateError || !updatedVehicle) {
        const message = updateError?.message || 'Unknown error';
        setError('Failed to update vehicle: ' + message);
        alert('Failed to update vehicle: ' + message);
        return;
      }
      setVehicles(vehicles.map(v => v.id === editingVehicle.id ? updatedVehicle : v));
      setShowAddModal(false);
      setEditingVehicle(null);
      resetForm();
      alert('Vehicle updated successfully!');
    } catch (err) {
      setError('Failed to update vehicle: ' + err.message);
      console.error('Error updating vehicle:', err);
      alert('Failed to update vehicle. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (window.confirm('Are you sure you want to delete this vehicle?')) {
      try {
        setLoading(true);
        await vehicleService.deleteVehicle(id);
        await loadVehicles();
      } catch (err) {
        console.error('Failed to delete vehicle:', err);
        setError('Failed to delete vehicle. It may have associated records.');
        alert('Failed to delete vehicle. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleQuickStatusUpdate = async (id, newStatus) => {
    const statusLabel = newStatus === 'sold' ? 'sold' : newStatus;
    if (window.confirm(`Mark this vehicle as ${statusLabel}?`)) {
      try {
        setLoading(true);
        await vehicleService.updateVehicleStatus(id, newStatus);
        await loadVehicles();
      } catch (err) {
        console.error('Failed to update vehicle status:', err);
        setError('Failed to update vehicle status');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setVehicleForm({
      make: '',
      model: '',
      year: new Date().getFullYear(),
      price: '',
      mileage: '',
      condition: 'new',
      status: 'on-lot',
      vehicleType: unitType,
      vin: '',
      carfaxUrl: '',
      stockNumber: '',
      fuelType: 'gasoline',
      transmission: 'automatic',
      exteriorColor: '',
      interiorColor: '',
      engine: '',
      drivetrain: 'fwd',
      description: '',
      features: [],
      images: [],
      videos: [],
      // Trailer-specific fields
      length: '',
      width: '',
      capacity: '',
      axles: '1',
      trailerType: 'utility'
    });
  };

  const handleFormChange = (field, value) => {
    setVehicleForm(prev => ({ ...prev, [field]: value }));
    // Clear VIN error when user types in VIN field
    if (field === 'vin') {
      setVinError(null);
    }
  };

  const handleDecodeVIN = async () => {
    if (!vehicleForm.vin || vehicleForm.vin.trim().length === 0) {
      setVinError('Please enter a VIN to decode');
      return;
    }

    setDecodingVIN(true);
    setVinError(null);

    try {
      const result = await vinDecoderService.decodeVIN(vehicleForm.vin);
      
      if (result.error) {
        setVinError(result.message);
        return;
      }

      // Auto-populate form fields with decoded data
      const decodedData = result.data;
      setVehicleForm(prev => ({
        ...prev,
        vin: decodedData.vin || prev.vin,
        make: decodedData.make || prev.make,
        model: decodedData.model || prev.model,
        year: decodedData.year || prev.year,
        trim: decodedData.trim || prev.trim,
        bodyType: decodedData.bodyType || prev.bodyType,
        doors: decodedData.doors || prev.doors,
        engine: decodedData.engineConfiguration && decodedData.engineLiters 
          ? `${decodedData.engineLiters}L ${decodedData.engineConfiguration}` 
          : (decodedData.engineModel || prev.engine),
        engineCylinders: decodedData.engineCylinders || prev.engineCylinders,
        engineHP: decodedData.engineHP || prev.engineHP,
        fuelType: decodedData.fuelType ? decodedData.fuelType.toLowerCase() : prev.fuelType,
        transmission: decodedData.transmission ? decodedData.transmission.toLowerCase() : prev.transmission,
        drivetrain: decodedData.drivetrain ? decodedData.drivetrain.toLowerCase() : prev.drivetrain,
        vehicleType: decodedData.vehicleType || prev.vehicleType,
        description: decodedData.description || prev.description,
        // Store additional decoded data
        decodedInfo: {
          manufacturerName: decodedData.manufacturerName,
          plantCity: decodedData.plantCity,
          plantState: decodedData.plantState,
          plantCountry: decodedData.plantCountry,
          abs: decodedData.abs,
          esc: decodedData.esc,
          tractionControl: decodedData.tractionControl,
          turbo: decodedData.turbo,
          seats: decodedData.seats,
          wheelbase: decodedData.wheelbase,
          gvwr: decodedData.gvwr
        }
      }));

      // Show success message
      alert('VIN decoded successfully! Form fields have been auto-populated.');
    } catch (error) {
      console.error('Error decoding VIN:', error);
      setVinError('Failed to decode VIN. Please try again.');
    } finally {
      setDecodingVIN(false);
    }
  };

  // Convert image to WebP (same function as ImageUpload)
  const convertToWebP = async (imageUrl, maxWidth = 1600, maxHeight = 1600, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        let { width, height } = img;
        
        if (width > maxWidth || height > maxHeight) {
          const aspectRatio = width / height;
          if (width > height) {
            if (width > maxWidth) { width = maxWidth; height = Math.round(width / aspectRatio); }
          } else {
            if (height > maxHeight) { height = maxHeight; width = Math.round(height * aspectRatio); }
          }
          if (width > maxWidth) { width = maxWidth; height = Math.round(width / aspectRatio); }
          if (height > maxHeight) { height = maxHeight; width = Math.round(height * aspectRatio); }
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Conversion failed')),
          'image/webp',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  // Batch optimize all vehicle images
  const handleBatchOptimize = async () => {
    setIsOptimizing(true);
    setShowOptimizeModal(true);
    
    // Get all vehicles with images
    const { data: allVehicles } = await vehicleService.getAllVehicles();
    const vehiclesWithImages = (allVehicles || []).filter(v => v.images && v.images.length > 0);
    
    setOptimizeProgress({ current: 0, total: vehiclesWithImages.length, currentVehicle: '', currentImage: 0, totalImages: 0, skipped: 0, converted: 0 });
    
    let totalConverted = 0;
    let totalSkipped = 0;
    
    for (let i = 0; i < vehiclesWithImages.length; i++) {
      const vehicle = vehiclesWithImages[i];
      const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
      
      setOptimizeProgress(prev => ({ 
        ...prev, 
        current: i + 1, 
        currentVehicle: vehicleName,
        totalImages: vehicle.images.length,
        currentImage: 0
      }));
      
      const newImageUrls = [];
      let vehicleUpdated = false;
      
      for (let j = 0; j < vehicle.images.length; j++) {
        const imageUrl = vehicle.images[j];
        
        setOptimizeProgress(prev => ({ ...prev, currentImage: j + 1 }));
        
        // Skip if already webp
        if (imageUrl.toLowerCase().endsWith('.webp')) {
          newImageUrls.push(imageUrl);
          totalSkipped++;
          setOptimizeProgress(prev => ({ ...prev, skipped: totalSkipped }));
          continue;
        }
        
        try {
          // Download and convert
          const blob = await convertToWebP(imageUrl);
          
          // Upload new webp
          const timestamp = Date.now();
          const fileName = `${vehicle.id}-${timestamp}-${j}.webp`;
          
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('vehicle-images')
            .upload(fileName, blob, { cacheControl: '3600', upsert: false, contentType: 'image/webp' });
          
          if (uploadError) throw uploadError;
          
          const { data: { publicUrl } } = supabase.storage
            .from('vehicle-images')
            .getPublicUrl(fileName);
          
          newImageUrls.push(publicUrl);
          vehicleUpdated = true;
          totalConverted++;
          setOptimizeProgress(prev => ({ ...prev, converted: totalConverted }));
          
          // Try to delete old image (extract filename from URL)
          try {
            const oldFileName = imageUrl.split('/').pop();
            if (oldFileName && !oldFileName.endsWith('.webp')) {
              await supabase.storage.from('vehicle-images').remove([oldFileName]);
            }
          } catch (deleteErr) {
            console.warn('Could not delete old image:', deleteErr);
          }
          
        } catch (err) {
          console.error(`Failed to convert image for ${vehicleName}:`, err);
          newImageUrls.push(imageUrl); // Keep original on error
          totalSkipped++;
          setOptimizeProgress(prev => ({ ...prev, skipped: totalSkipped }));
        }
      }
      
      // Update vehicle with new image URLs if any were converted
      if (vehicleUpdated) {
        try {
          await vehicleService.updateVehicle(vehicle.id, { images: newImageUrls });
        } catch (updateErr) {
          console.error(`Failed to update vehicle ${vehicle.id}:`, updateErr);
        }
      }
    }
    
    setIsOptimizing(false);
    // Reload vehicles to show updated data
    loadVehicles();
  };

  return (
    <div className="space-y-6 bg-white">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{getUnitTitle()} Management</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage your {getUnitTitle().toLowerCase()} inventory</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={handleBatchOptimize}
            disabled={isOptimizing}
            className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
            title="Convert all images to WebP format for faster loading"
          >
            <FaUpload className="w-4 h-4" />
            <span>Optimize Images</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Add {getUnitTitle().slice(0, -1)}</span>
          </button>
        </div>
      </div>

      {/* Image Optimization Modal */}
      {showOptimizeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {isOptimizing ? (
                  <svg className="animate-spin h-8 w-8 text-green-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {isOptimizing ? 'Optimizing Images...' : 'Optimization Complete!'}
              </h3>
              {isOptimizing && (
                <p className="text-gray-600 text-sm">
                  Please keep this tab open until complete
                </p>
              )}
            </div>

            {/* Progress Stats */}
            <div className="space-y-4">
              {/* Vehicle Progress */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Vehicles</span>
                  <span className="font-medium">{optimizeProgress.current} / {optimizeProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${optimizeProgress.total ? (optimizeProgress.current / optimizeProgress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Current Vehicle */}
              {isOptimizing && optimizeProgress.currentVehicle && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Currently processing:</p>
                  <p className="font-medium text-gray-900 truncate">{optimizeProgress.currentVehicle}</p>
                  <p className="text-xs text-gray-500">Image {optimizeProgress.currentImage} of {optimizeProgress.totalImages}</p>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{optimizeProgress.converted}</p>
                  <p className="text-xs text-green-700">Converted to WebP</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-600">{optimizeProgress.skipped}</p>
                  <p className="text-xs text-gray-500">Already Optimized</p>
                </div>
              </div>
            </div>

            {/* Close Button */}
            {!isOptimizing && (
              <button
                onClick={() => setShowOptimizeModal(false)}
                className="mt-6 w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium">{error}</p>
              <button 
                onClick={() => setError(null)}
                className="mt-2 text-sm text-red-600 hover:text-white underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
            <p className="text-sm font-medium">Processing...</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search vehicles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="in-transit">In Transit</option>
            <option value="recon-pending">Recon Pending</option>
            <option value="on-lot">On Lot</option>
            <option value="pending-contract">Pending Contract</option>
            <option value="sold">Sold</option>
          </select>
          <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-600 sm:col-span-2 lg:col-span-1">
            <SafeIcon icon={FiFilter} className="w-4 h-4" />
            <span>Showing {filteredVehicles.length} of {vehicles.length} vehicles</span>
          </div>
        </div>
      </div>

      {/* Vehicle List - Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock/VIN
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mileage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVehicles.map((vehicle) => (
                <tr key={vehicle.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div 
                        className="h-12 w-12 bg-gray-200 rounded-lg mr-4 overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary-500 transition-all"
                        onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                        title="View vehicle details"
                      >
                        {vehicle?.images?.[0] && (
                          <img 
                            src={vehicle.images[0]} 
                            alt={`${vehicle.make} ${vehicle.model}`}
                            className="h-full w-full object-cover"
                          />
                        )}
                      </div>
                      <div 
                        className="cursor-pointer hover:text-primary-600 transition-colors"
                        onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                      >
                        <div className="text-sm font-medium text-gray-900">
                          {formatVehicleTitle(vehicle)}
                        </div>
                        <div className="text-sm text-gray-500">{vehicle.condition}</div>
                        {unitType === 'trailer' && (
                          <div className="text-xs text-gray-600">
                            {vehicle.length && `${vehicle.length}'`} 
                            {vehicle.width && ` x ${vehicle.width}'`}
                            {vehicle.capacity && ` • ${vehicle.capacity} lbs`}
                          </div>
                        )}
                        {vehicle.vin && <div className="text-xs text-gray-400 font-mono">VIN: {vehicle.vin}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {vehicle.stock_number || vehicle.stockNumber || 'N/A'}
                    </div>
                    {vehicle.vin && (
                      <div className="text-xs text-gray-500 font-mono truncate" style={{maxWidth: '150px'}} title={vehicle.vin}>
                        {vehicle.vin}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      ${(vehicle.price || 0).toLocaleString()}
                      {vehicle.deposit_amount && (
                        <div className="text-xs text-gray-500">
                          Deposit: ${vehicle.deposit_amount.toLocaleString()}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(vehicle.mileage || 0).toLocaleString()} mi
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      vehicle.status === 'on-lot' ? 'bg-green-100 text-green-800' :
                      vehicle.status === 'pending-contract' ? 'bg-black text-white' :
                      vehicle.status === 'sold' ? 'bg-cyan-100 text-cyan-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {vehicle.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditVehicle(vehicle)}
                        className="text-primary-600 hover:text-primary-700"
                        title="Edit"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      {vehicle.status !== 'sold' && (
                        <button
                          onClick={() => handleQuickStatusUpdate(vehicle.id, 'sold')}
                          className="text-green-600 hover:text-green-700"
                          title="Mark as Sold"
                        >
                          <SafeIcon icon={FiCheck} className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteVehicle(vehicle.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vehicle List - Mobile Cards */}
      <div className="md:hidden space-y-4">
        {filteredVehicles.map((vehicle) => (
          <div key={vehicle.id} className="bg-white rounded-lg shadow-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div
                  className="h-16 w-16 rounded-xl overflow-hidden flex-shrink-0 bg-white/60 shadow-[inset_6px_6px_12px_#c7d2ea,inset_-6px_-6px_12px_#ffffff]"
                  onClick={() => navigate(`/vehicle/${vehicle.id}`)}
                >
                  {vehicle.images[0] && (
                    <img 
                      src={vehicle.images[0]} 
                      alt={`${vehicle.make} ${vehicle.model}`}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {formatVehicleTitle(vehicle)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Stock: {vehicle.stock_number || vehicle.stockNumber || 'N/A'}
                  </div>
                  {unitType === 'trailer' && (
                    <div className="text-xs text-gray-600 mt-1">
                      {vehicle.length && `${vehicle.length}' L`} 
                      {vehicle.width && ` x ${vehicle.width}' W`}
                      {vehicle.capacity && ` • ${vehicle.capacity} lbs`}
                    </div>
                  )}
                  {vehicle.vin && (
                    <div className="text-xs text-gray-400 font-mono mt-1">
                      VIN: {vehicle.vin}
                    </div>
                  )}
                </div>
              </div>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                vehicle.status === 'on-lot' ? 'bg-green-100 text-green-800' :
                vehicle.status === 'pending-contract' ? 'bg-black text-white' :
                vehicle.status === 'sold' ? 'bg-cyan-100 text-cyan-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {vehicle.status}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <div>
                <span className="text-gray-500">Price:</span>
                <span className="ml-1 font-medium">${(vehicle.price || 0).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-500">Mileage:</span>
                <span className="ml-1 font-medium">{(vehicle.mileage || 0).toLocaleString()} mi</span>
              </div>
              {vehicle.deposit_amount && (
                <>
                  <div className="col-span-2">
                    <span className="text-gray-500">Deposit Required:</span>
                    <span className="ml-1 font-medium text-primary-600">
                      ${vehicle.deposit_amount.toLocaleString()}
                    </span>
                    {vehicle.vehicleType === 'golf-cart' && (
                      <span className="ml-1 text-xs text-gray-400">(Golf Cart)</span>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => handleEditVehicle(vehicle)}
                className="px-3 py-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                title="Edit"
              >
                <SafeIcon icon={FiEdit} className="w-4 h-4" />
              </button>
              {vehicle.status !== 'sold' && (
                <button
                  onClick={() => handleQuickStatusUpdate(vehicle.id, 'sold')}
                  className="px-3 py-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="Mark as Sold"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => handleDeleteVehicle(vehicle.id)}
                className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Vehicle Modal */}
      {(showAddModal || editingVehicle) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white sm:rounded-xl shadow-lg max-w-6xl w-full max-h-[99vh] overflow-y-auto">
            <div className="p-4 sm:p-32 border-b border-gray-200 relative">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                {editingVehicle ? `Edit ${getUnitTitle().slice(0, -1)}` : `Add New ${getUnitTitle().slice(0, -1)}`}
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingVehicle(null);
                  resetForm();
                }}
                className="absolute top-4 right-4 text-red-600 hover:text-red-700 transition-colors"
              >
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Golf Cart Specific Form */}
              {unitType === 'golf-cart' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                    <input
                      type="text"
                      value={vehicleForm.make}
                      onChange={(e) => handleFormChange('make', e.target.value)}
                      className="form-input"
                      placeholder="Club Car, EZGO, Yamaha..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                    <input
                      type="text"
                      value={vehicleForm.model}
                      onChange={(e) => handleFormChange('model', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                    <input
                      type="number"
                      value={vehicleForm.year}
                      onChange={(e) => handleFormChange('year', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input
                      type="number"
                      value={vehicleForm.price}
                      onChange={(e) => handleFormChange('price', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Seats</label>
                    <select
                      value={vehicleForm.seats || '2'}
                      onChange={(e) => handleFormChange('seats', e.target.value)}
                      className="form-input"
                    >
                      <option value="2">2 Seats</option>
                      <option value="4">4 Seats</option>
                      <option value="6">6 Seats</option>
                      <option value="8">8 Seats</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Power Type</label>
                    <select
                      value={vehicleForm.fuelType || 'electric'}
                      onChange={(e) => handleFormChange('fuelType', e.target.value)}
                      className="form-input"
                    >
                      <option value="electric">Electric</option>
                      <option value="gas">Gas</option>
                      <option value="lithium">Lithium Battery</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Top Speed (mph)</label>
                    <input
                      type="number"
                      value={vehicleForm.topSpeed || ''}
                      onChange={(e) => handleFormChange('topSpeed', e.target.value)}
                      className="form-input"
                      placeholder="15-25 mph"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Range (miles)</label>
                    <input
                      type="number"
                      value={vehicleForm.range || ''}
                      onChange={(e) => handleFormChange('range', e.target.value)}
                      className="form-input"
                      placeholder="30-50 miles"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number / VIN (Optional)</label>
                    <input
                      type="text"
                      value={vehicleForm.vin || ''}
                      onChange={(e) => handleFormChange('vin', e.target.value)}
                      className="form-input"
                      placeholder="Enter serial number or VIN"
                      maxLength="50"
                    />
                  </div>
                </div>
              ) : unitType === 'boat' ? (
                /* Boat Specific Form */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                    <input
                      type="text"
                      value={vehicleForm.make}
                      onChange={(e) => handleFormChange('make', e.target.value)}
                      className="form-input"
                      placeholder="Sea Ray, Bayliner, etc..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                    <input
                      type="text"
                      value={vehicleForm.model}
                      onChange={(e) => handleFormChange('model', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                    <input
                      type="number"
                      value={vehicleForm.year}
                      onChange={(e) => handleFormChange('year', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input
                      type="number"
                      value={vehicleForm.price}
                      onChange={(e) => handleFormChange('price', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Length (ft)</label>
                    <input
                      type="number"
                      value={vehicleForm.length || ''}
                      onChange={(e) => handleFormChange('length', e.target.value)}
                      className="form-input"
                      placeholder="Length in feet"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Engine Type</label>
                    <select
                      value={vehicleForm.engine || 'outboard'}
                      onChange={(e) => handleFormChange('engine', e.target.value)}
                      className="form-input"
                    >
                      <option value="outboard">Outboard</option>
                      <option value="inboard">Inboard</option>
                      <option value="stern-drive">Stern Drive</option>
                      <option value="jet">Jet Drive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hull Material</label>
                    <select
                      value={vehicleForm.hullMaterial || 'fiberglass'}
                      onChange={(e) => handleFormChange('hullMaterial', e.target.value)}
                      className="form-input"
                    >
                      <option value="fiberglass">Fiberglass</option>
                      <option value="aluminum">Aluminum</option>
                      <option value="steel">Steel</option>
                      <option value="wood">Wood</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hours</label>
                    <input
                      type="number"
                      value={vehicleForm.mileage || ''}
                      onChange={(e) => handleFormChange('mileage', parseInt(e.target.value))}
                      className="form-input"
                      placeholder="Engine hours"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Serial Number / VIN</label>
                    <input
                      type="text"
                      value={vehicleForm.vin || ''}
                      onChange={(e) => handleFormChange('vin', e.target.value)}
                      className="form-input"
                      placeholder="Enter serial number or VIN (optional)"
                    />
                  </div>
                </div>
              ) : unitType === 'trailer' ? (
                /* Trailer Specific Form */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                    <input
                      type="text"
                      value={vehicleForm.make}
                      onChange={(e) => handleFormChange('make', e.target.value)}
                      className="form-input"
                      placeholder="Big Tex, PJ, Load Trail..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                    <input
                      type="text"
                      value={vehicleForm.model}
                      onChange={(e) => handleFormChange('model', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                    <input
                      type="number"
                      value={vehicleForm.year}
                      onChange={(e) => handleFormChange('year', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input
                      type="number"
                      value={vehicleForm.price}
                      onChange={(e) => handleFormChange('price', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trailer Type</label>
                    <select
                      value={vehicleForm.trailerType || 'utility'}
                      onChange={(e) => handleFormChange('trailerType', e.target.value)}
                      className="form-input"
                    >
                      <option value="utility">Utility</option>
                      <option value="enclosed">Enclosed</option>
                      <option value="flatbed">Flatbed</option>
                      <option value="dump">Dump</option>
                      <option value="car-hauler">Car Hauler</option>
                      <option value="equipment">Equipment</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Length (ft)</label>
                    <input
                      type="number"
                      value={vehicleForm.length || ''}
                      onChange={(e) => handleFormChange('length', e.target.value)}
                      className="form-input"
                      placeholder="Deck length"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Width (ft)</label>
                    <input
                      type="number"
                      value={vehicleForm.width || ''}
                      onChange={(e) => handleFormChange('width', e.target.value)}
                      className="form-input"
                      placeholder="Deck width"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Weight Capacity (lbs)</label>
                    <input
                      type="number"
                      value={vehicleForm.capacity || ''}
                      onChange={(e) => handleFormChange('capacity', e.target.value)}
                      className="form-input"
                      placeholder="GVWR"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
                    <input
                      type="number"
                      value={vehicleForm.mileage || ''}
                      onChange={(e) => handleFormChange('mileage', e.target.value)}
                      className="form-input"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Axles</label>
                    <select
                      value={vehicleForm.axles || '1'}
                      onChange={(e) => handleFormChange('axles', e.target.value)}
                      className="form-input"
                    >
                      <option value="1">Single Axle</option>
                      <option value="2">Tandem Axle</option>
                      <option value="3">Triple Axle</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">VIN / Serial Number</label>
                    <input
                      type="text"
                      value={vehicleForm.vin || ''}
                      onChange={(e) => handleFormChange('vin', e.target.value)}
                      className="form-input"
                      placeholder="Enter VIN or serial number (optional)"
                    />
                  </div>
                </div>
              ) : (
                /* Default Vehicle Form */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Make *</label>
                    <input
                      type="text"
                      value={vehicleForm.make}
                      onChange={(e) => handleFormChange('make', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Model *</label>
                    <input
                      type="text"
                      value={vehicleForm.model}
                      onChange={(e) => handleFormChange('model', e.target.value)}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Year *</label>
                    <input
                      type="number"
                      value={vehicleForm.year}
                      onChange={(e) => handleFormChange('year', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Price *</label>
                    <input
                      type="number"
                      value={vehicleForm.price}
                      onChange={(e) => handleFormChange('price', parseInt(e.target.value))}
                      className="form-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mileage</label>
                    <input
                      type="number"
                      value={vehicleForm.mileage}
                      onChange={(e) => handleFormChange('mileage', parseInt(e.target.value))}
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stock Number *</label>
                  <input
                    type="text"
                    value={vehicleForm.stockNumber}
                    onChange={(e) => handleFormChange('stockNumber', e.target.value)}
                    className="form-input"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {unitType === 'golf-cart' || unitType === 'trailer' ? 'Serial Number / VIN (Optional)' : 'VIN (Vehicle Identification Number)'}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={vehicleForm.vin}
                      onChange={(e) => handleFormChange('vin', e.target.value)}
                      className="form-input flex-1"
                      placeholder={unitType === 'golf-cart' ? 'Enter serial number (optional)' : unitType === 'trailer' ? 'Enter VIN or serial number (optional)' : 'Enter 17-character VIN'}
                      maxLength={unitType === 'vehicle' ? "17" : "50"}
                    />
                    {unitType === 'vehicle' && (
                      <button
                        type="button"
                        onClick={handleDecodeVIN}
                        disabled={decodingVIN || !vehicleForm.vin}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors flex items-center gap-2"
                      >
                        {decodingVIN ? (
                          <>
                            <FiRefreshCw className="animate-spin" />
                            Decoding...
                          </>
                        ) : (
                          <>
                            <FiRefreshCw />
                            Decode VIN
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {vinError && (
                    <p className="mt-2 text-sm text-red-600">{vinError}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {unitType === 'golf-cart' ? 'Optional: Enter the golf cart serial number for tracking' :
                     unitType === 'trailer' ? 'Optional: Enter the trailer VIN or serial number if available' :
                     'Enter a VIN and click "Decode" to auto-populate vehicle details from NHTSA database'}
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">CARFAX / Vehicle History Report Link (Optional)</label>
                  <input
                    type="url"
                    value={vehicleForm.carfaxUrl || ''}
                    onChange={(e) => handleFormChange('carfaxUrl', e.target.value)}
                    className="form-input"
                    placeholder="https://www.vin-reports-pro.com/share/..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Paste the shareable vehicle history report URL. When set, a "View Vehicle History" link appears on this vehicle's detail page.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Engine</label>
                  <input
                    type="text"
                    value={vehicleForm.engine || ''}
                    onChange={(e) => handleFormChange('engine', e.target.value)}
                    className="form-input"
                    placeholder="e.g., 5.3L V8, 2.0L 4-Cylinder"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Drivetrain</label>
                  <select
                    value={vehicleForm.drivetrain || ''}
                    onChange={(e) => handleFormChange('drivetrain', e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Drivetrain</option>
                    <option value="FWD">FWD (Front-Wheel Drive)</option>
                    <option value="RWD">RWD (Rear-Wheel Drive)</option>
                    <option value="AWD">AWD (All-Wheel Drive)</option>
                    <option value="4WD">4WD (4-Wheel Drive)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Exterior Color</label>
                  <input
                    type="text"
                    value={vehicleForm.exteriorColor || ''}
                    onChange={(e) => handleFormChange('exteriorColor', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Silver, Black, White"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interior Color</label>
                  <input
                    type="text"
                    value={vehicleForm.interiorColor || ''}
                    onChange={(e) => handleFormChange('interiorColor', e.target.value)}
                    className="form-input"
                    placeholder="e.g., Black, Tan, Gray"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
                  <select
                    value={vehicleForm.condition}
                    onChange={(e) => handleFormChange('condition', e.target.value)}
                    className="form-select"
                  >
                    <option value="new">New</option>
                    <option value="used">Used</option>
                    <option value="professional">Professional Pre-Owned</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
                  <select
                    value={vehicleForm.vehicleType}
                    onChange={(e) => handleFormChange('vehicleType', e.target.value)}
                    className="form-select"
                  >
                    <option value="vehicle">Vehicle</option>
                    <option value="golf-cart">Golf Cart</option>
                    <option value="trailer">Trailer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={vehicleForm.status}
                    onChange={(e) => handleFormChange('status', e.target.value)}
                    className="form-select"
                  >
                    <option value="in-transit">In Transit</option>
                    <option value="recon-pending">Recon Pending</option>
                    <option value="on-lot">On Lot</option>
                    <option value="pending-contract">Pending Contract</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>
              </div>
              )}

              {/* Common fields for all unit types */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={vehicleForm.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={4}
                  className="form-input"
                  placeholder="Enter description..."
                />
              </div>

              {/* Image Upload */}
              <ImageUpload
                images={vehicleForm.images}
                onImagesChange={(newImages) => handleFormChange('images', newImages)}
                vehicleId={editingVehicle?.id || `new-${Date.now()}`}
                vehicleInfo={{ year: vehicleForm.year, make: vehicleForm.make, model: vehicleForm.model }}
              />

              {/* Video Upload */}
              <VideoUpload
                vehicleId={editingVehicle?.id || `new-${Date.now()}`}
                onVideoUploaded={(urlOrUrls, deletedUrl, isReorder) => {
                  try {
                    // Ensure videos array exists
                    const currentVideos = vehicleForm.videos || [];
                    
                    if (isReorder && Array.isArray(urlOrUrls)) {
                      // Handle reordering - replace entire array with new order
                      handleFormChange('videos', urlOrUrls);
                      console.log('Videos reordered');
                    } else if (deletedUrl) {
                      // Remove deleted video
                      const newVideos = currentVideos.filter(v => v !== deletedUrl);
                      handleFormChange('videos', newVideos);
                      console.log('Video deleted, remaining:', newVideos.length);
                    } else if (urlOrUrls) {
                      // Handle both single URL (from delete) and array (from upload)
                      if (Array.isArray(urlOrUrls)) {
                        // Multiple URLs from batch upload
                        handleFormChange('videos', [...currentVideos, ...urlOrUrls]);
                        console.log(`Added ${urlOrUrls.length} videos to form`);
                      } else {
                        // Single URL (legacy/fallback)
                        handleFormChange('videos', [...currentVideos, urlOrUrls]);
                      }
                    }
                  } catch (error) {
                    console.error('Error updating videos:', error);
                    alert('An error occurred while updating videos. Please refresh and try again.');
                  }
                }}
                existingVideos={vehicleForm.videos || []}
              />
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-0 sm:space-x-4">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingVehicle(null);
                  resetForm();
                }}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={editingVehicle ? handleUpdateVehicle : handleAddVehicle}
                className="w-full sm:w-auto px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AddVehicleForm removed - using the comprehensive internal form above */}
    </div>
  );
};

export default VehicleManager;